/**
 * Dashboard Jobs › Active, Story and Boards (spec §2.2 tab 3; W4b card C3, D-617). Read-only in flight: Active has
 * **Set course** per job and the hand-in station as a docked-only note; Boards shows each known job board as last
 * seen, with Accept disabled ("Accept at <station>'s board") and Set course to that station. Story shows the
 * objective line and opens Log › Case file for the leads and questions.
 */
import { useState, type ReactElement } from 'react'
import type { Career } from '../../../../career/types'
import { FocusButton, FocusContext } from '../../../focus'
import type { FocusGroup } from '../../../nav'
import type { ReachCommandResult } from '../../../../reach/types'
import { AgeStamp, DockedAction } from '../../parts'
import { loadDashboardConfig } from '../../config'
import { toNearestStation, type StationDirectory } from '../../data'
import type { NearestStation } from '../../parts'
import type { DashContext, DashSection, DashSectionProps } from '../../registry'
import { useDashFocus } from '../../useDashFocus'
import { useOpenRefresh } from './live'
import { activeJobsModel, boardsModel, fill, loadJobsText, storyModel, type ActiveJobsDeps, type BoardsDeps, type CourseTarget } from './model'

export interface JobsSectionDeps {
  career(): Career | null
  gameTime(): number
  dir(): StationDirectory | null
  activeDeps(): ActiveJobsDeps
  boardsDeps(): BoardsDeps | null
  objective(): string
  /** Set course to a job's target; the result's message shows under the list. */
  course(target: CourseTarget): ReachCommandResult
  /** A target's display name for the result line. */
  targetName(target: CourseTarget): string
}

const careerOnly = (ctx: DashContext): boolean => ctx.career

function useCourse(deps: JobsSectionDeps): [string, (t: CourseTarget) => void] {
  const [line, setLine] = useState('')
  const t = loadJobsText()
  return [
    line,
    (target: CourseTarget): void => {
      const r = deps.course(target)
      setLine(r.ok ? fill(t.active.courseSet, { target: deps.targetName(target) }) : r.message)
    },
  ]
}

function ActiveSection(deps: JobsSectionDeps, p: DashSectionProps): ReactElement {
  useOpenRefresh()
  const t = loadJobsText()
  const career = deps.career()
  const dir = deps.dir()
  const rows = career === null || dir === null ? [] : activeJobsModel(career, deps.gameTime(), dir, deps.activeDeps(), t)
  const groups: FocusGroup[] = [{ id: 'list', count: Math.max(1, rows.length) }]
  const focus = useDashFocus(p.nav, groups)
  const [line, course] = useCourse(deps)
  if (rows.length === 0) return <p className="dash-empty">{t.active.empty}</p>
  return (
    <FocusContext.Provider value={focus}>
      <ul className="dash-list dash-jobs" data-dash-jobs="active">
        {rows.map((r, i) => (
          <li key={r.id} className="dash-row" data-job={r.id}>
            <p className="dash-row__title">
              <strong>{r.typeLabel}</strong> · {r.requirement}
            </p>
            <p className="dash-row__meta">
              <span className={r.expired ? 'dash-warn' : undefined}>{r.ready ? t.active.ready : r.timeLeft}</span> · {r.reward}
            </p>
            <p className="dash-row__meta">{r.handIn}</p>
            {r.course === null ? (
              <p className="dash-row__meta">{t.active.noCourse}</p>
            ) : (
              <FocusButton group="list" index={i} testId={`dash-job-course-${i}`} onActivate={() => r.course !== null && course(r.course)}>
                <kbd>F</kbd> {loadDashboardConfig().dockedAction.setCourse}
              </FocusButton>
            )}
          </li>
        ))}
      </ul>
      {line !== '' ? (
        <p className="dash-row__meta" data-dash-course-result="">
          {line}
        </p>
      ) : null}
    </FocusContext.Provider>
  )
}

function StorySection(deps: JobsSectionDeps, p: DashSectionProps): ReactElement {
  useOpenRefresh()
  const t = loadJobsText()
  const m = storyModel(deps.objective())
  const focus = useDashFocus(p.nav, [{ id: 'list', count: 1 }])
  return (
    <FocusContext.Provider value={focus}>
      <div className="dash-jobs" data-dash-jobs="story">
        <h3 className="dash-row__title">{t.story.objective}</h3>
        <p data-story-objective="">{m.objective === '' ? t.story.none : m.objective}</p>
        <FocusButton group="list" index={0} onActivate={() => p.port.open({ tab: 'log', section: 'log.caseFile' })}>
          {t.story.caseFile}
        </FocusButton>
      </div>
    </FocusContext.Provider>
  )
}

function nearestOf(dir: StationDirectory | null, stationId: string): NearestStation | null {
  const station = dir?.stations.find((s) => s.id === stationId)
  if (dir === null || station === undefined) return null
  const jumps = dir.jumpsTo(station.systemId)
  return jumps === null ? null : toNearestStation(loadDashboardConfig().dockedAction, { station, jumps, distanceM: null })
}

function BoardsSection(deps: JobsSectionDeps, p: DashSectionProps): ReactElement {
  useOpenRefresh()
  const t = loadJobsText()
  const cfg = loadDashboardConfig()
  const dir = deps.dir()
  const bd = deps.boardsDeps()
  const rows = dir === null || bd === null ? [] : boardsModel(dir, bd, t)
  const focus = useDashFocus(p.nav, [{ id: 'list', count: Math.max(1, rows.length) }])
  const [line, course] = useCourse(deps)
  if (rows.length === 0) return <p className="dash-empty">{t.boards.empty}</p>
  return (
    <FocusContext.Provider value={focus}>
      <ul className="dash-list dash-jobs" data-dash-jobs="boards">
        {rows.map((r) => (
          <li key={r.stationId} className="dash-row" data-board={r.stationId}>
            <p className="dash-row__title">
              <strong>{r.station}</strong>
              {r.age.level !== 'never' ? ` · ${fill(t.boards.offers, { n: String(r.offers.length) })}` : ''}
            </p>
            <AgeStamp age={r.age} />
            {r.age.level !== 'never' && r.offers.length === 0 ? <p className="dash-row__meta">{t.boards.noOffers}</p> : null}
            {r.offers.map((o) => (
              <p key={o.id} className="dash-row__meta">
                <strong>{o.typeLabel}</strong> · {o.requirement} · {o.reward}
              </p>
            ))}
            <DockedAction
              cfg={cfg.dockedAction}
              label={t.boards.accept}
              reason={r.acceptAt}
              nearest={nearestOf(dir, r.stationId)}
              onSetCourse={() => course({ kind: 'station', id: r.stationId })}
            />
          </li>
        ))}
      </ul>
      {line !== '' ? (
        <p className="dash-row__meta" data-dash-course-result="">
          {line}
        </p>
      ) : null}
    </FocusContext.Provider>
  )
}

/** The three Jobs sections, in order (ids `jobs.active`, `jobs.story`, `jobs.boards`). */
export function createJobsSections(deps: JobsSectionDeps): DashSection[] {
  const t = loadJobsText()
  const Active = (p: DashSectionProps): ReactElement => ActiveSection(deps, p)
  const Story = (p: DashSectionProps): ReactElement => StorySection(deps, p)
  const Boards = (p: DashSectionProps): ReactElement => BoardsSection(deps, p)
  return [
    { id: 'jobs.active', tab: 'jobs', order: 10, label: t.sections.active, available: careerOnly, component: Active },
    { id: 'jobs.story', tab: 'jobs', order: 20, label: t.sections.story, available: careerOnly, component: Story },
    { id: 'jobs.boards', tab: 'jobs', order: 30, label: t.sections.boards, available: careerOnly, component: Boards },
  ]
}
